import { useState } from 'react'
import ButtonSolid from '../Components/ButtonSolid'
import ButtonOutline from '../Components/ButtonOutline'
import toast from 'react-hot-toast'
import MODsAssemblyList from '../Components/MODsAssemblyList'
import TemplateHeaders from '../Components/TemplateHeaders'
import CustomerDetailForm from '../Components/CustomerDetailForm'
import OverlayLoading from '../Components/OverlayLoading'
import TemplateAssemblyList from '../Components/TemplateAssemblyList'
import OrderModelDetails from '../Components/OrderModelDetails'
import OrderMainAssembly from '../Components/OrderMainAssembly'
import { pickModelDescription } from '../utils/orderModelDescription'

function UseEditTemplate({
  savingMachineOrders,
  originalSelectedAssembly,
  templateModelDesc,
  templateDetails,
  setTemplateDetails,
  originalTemplateDetails,
  fetchingTemplateDetails,
  accountNumberOptions,
  setAccountNumberOptions,
  selectedAssembly,
  setSelectedAssembly,
  mods_data,
  setMods_data,
  customer_data,
  setCustomer_data,
  assignedQR,
  setAssignedQR,
  validateFields,
  saveChanges,
  originalCustomerData,
  originalModsData,
  setQR,
  editOrder,
  assemblyFilter,   // "Buyout" when in buyout mode, null otherwise
  // create order: the template's main assemblies, of which exactly one is chosen
  mainOptions,
  selectedMainId,
  setSelectedMainId,
  // edit order: the order's main assembly (its first assembly), locked unless replaced
  mainAssembly,
  setMainAssembly,
  originalMainAssembly,
  fallbackModelDescription,   // edit order: the template's model description, used when a new main has none
}) {
  const [toggleSearchPopup, setToggleSearchPopup] = useState(false)
  const [disableReset, setDisableReset] = useState(true)
  const [addedItems, setAddedItems] = useState([])
  const [changingMain, setChangingMain] = useState(false)

  // Buyout orders have no model details and no main assembly
  const isBuyoutOrder = assemblyFilter === "Buyout" || Boolean(templateModelDesc?.is_buyout)
  const currentMainId = editOrder ? mainAssembly?._id : selectedMainId
  // while true the list on the right picks the new main assembly instead of adding sub assemblies
  const selectingMain = editOrder && !isBuyoutOrder && changingMain

  const resetChanges = () => {
    setTemplateDetails(originalTemplateDetails)
    setSelectedAssembly(originalSelectedAssembly)
    if (!isBuyoutOrder) {
      if (editOrder) {
        setMainAssembly(originalMainAssembly)
        setChangingMain(false)
      } else {
        setSelectedMainId(mainOptions?.[0]?._id || null)
      }
    }
    if (editOrder) {
      setMods_data(originalModsData)
      setCustomer_data(originalCustomerData)
    } else {
      setMods_data({
        sales_order_number: '',
        customer_po: '',
        ...(isBuyoutOrder ? {} : {
          model_number: templateModelDesc?.name || '',
          model_description: pickModelDescription(mainOptions?.[0], templateModelDesc?.model_description),
        }),
      })
      setCustomer_data({
        account_number: '',
        name: '',
        company_name: '',
        primary_contact: '',
        primary_email: '',
        accounting_contact: '',
        accounting_email: '',
        bill_to: '',
        ship_to: '',
        others: '',
        shipment_date: '',
      })
    }
    setDisableReset(true)
  }

  // Edit order: replace the main assembly. The old main is dropped from the order; a sub
  // assembly picked as the new main moves out of the sub list.
  const replaceMainAssembly = (dataSet) => {
    if (dataSet._id !== mainAssembly?._id) {
      setTemplateDetails(prev =>
        Object.fromEntries(
          Object.entries(prev).map(([type, items]) => [type, items.filter(item => item._id !== dataSet._id)])
        )
      )
      setSelectedAssembly(prev => prev.filter(id => id !== dataSet._id))
      setMainAssembly(dataSet)
      // The model description follows the main assembly; the template's is used if the new main has
      // none (and, if the template can't be found either, the current one stays)
      setMods_data(prev => ({ ...prev, model_description: pickModelDescription(dataSet, fallbackModelDescription || prev.model_description) }))
      setDisableReset(false)
    }
    setChangingMain(false)
    setToggleSearchPopup(false)
  }

  const addAssemblyData = (dataSet) => {
    if (!isBuyoutOrder) {
      if (dataSet._id === currentMainId) {
        toast.error("This is already the main assembly")
        return
      }
      if (!editOrder && mainOptions?.some(option => option._id === dataSet._id)) {
        toast.error("This is a main assembly of the template. Choose it as the main assembly instead")
        return
      }
    }
    const assemblyType = dataSet.assembly_type;
    const assemblyToAdd = originalTemplateDetails[assemblyType]?.findIndex(item => item?._id === dataSet._id);
    const indexToAdd = templateDetails[assemblyType]?.findIndex(item => item?._id === dataSet._id);
    if (((assemblyToAdd === -1) || (originalTemplateDetails[assemblyType] === undefined)) & ((indexToAdd === -1) || (templateDetails[assemblyType] === undefined))) {
      setAddedItems(prevAddedItems => [...prevAddedItems, dataSet._id]);
    }
    if (assemblyFilter === "Buyout") {
      setSelectedAssembly(prevSelectedAssembly => [...prevSelectedAssembly, dataSet._id]);
    }
    if ((indexToAdd === -1) || (templateDetails[assemblyType] === undefined)) {
      setTemplateDetails(prevState => ({
        ...prevState, // Clone the existing state
        [assemblyType]: [ // Add new data under the respective assembly type
          ...(prevState[assemblyType] || []), // Clone existing data for the assembly type, if any
          dataSet // Add the new data from the dataSet
        ]
      }));
      setDisableReset(false);
      const element = document.getElementById(assemblyType);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    else {
      toast.error("Assembly Already Exist")
    }
  };

  return (
    <div>
      {savingMachineOrders && (
        <OverlayLoading loadingText='Saving Order Changes...' />
      )}
      <div className='flex w-full lg:h-[calc(100vh-80px)]'>
        <div className='w-full lg:pr-3 overflow-y-auto'>
          <div className='flex flex-wrap items-start md:items-center justify-between py-3 md:py-5 gap-2'>
            <TemplateHeaders templateModelDesc={templateModelDesc} loading={fetchingTemplateDetails} machineOrder={true} />
            <div className='mr-0 ml-auto'>
              <ButtonOutline
                buttonText={"Reset Changes"}
                disabled={disableReset}
                className="mr-4 mb-2"
                onClick={() => resetChanges()}
              />
              <ButtonSolid
                buttonText={`${editOrder ? "Save Changes" : "Create Order"}`}
                disabled={fetchingTemplateDetails}
                onClick={() => saveChanges()}
              />
            </div>
          </div>
          <div id='OrderForn'>
            <CustomerDetailForm
              loadingData={fetchingTemplateDetails}
              mods_data={mods_data}
              customer_data={customer_data}
              accountNumberOptions={accountNumberOptions}
              setAccountNumberOptions={setAccountNumberOptions}
              setMods_data={setMods_data}
              setCustomer_data={setCustomer_data}
              validateFields={validateFields}
              assignedQR={assignedQR}
              setAssignedQR={setAssignedQR}
              setQR={setQR}
              editOrder={editOrder}
              setDisableReset={setDisableReset}
              originalCustomerData={originalCustomerData}
            />
            {!isBuyoutOrder && (
              <OrderModelDetails
                loadingData={fetchingTemplateDetails}
                mods_data={mods_data}
                setMods_data={setMods_data}
                validateFields={validateFields}
                setDisableReset={setDisableReset}
              />
            )}
          </div>
          {!isBuyoutOrder && (
            <OrderMainAssembly
              loading={fetchingTemplateDetails}
              mode={editOrder ? 'edit' : 'choose'}
              options={mainOptions}
              selectedId={selectedMainId}
              onSelect={(id) => {
                setSelectedMainId(id)
                setDisableReset(false)
                // The model description follows the chosen main assembly (the template's if it has none)
                const option = mainOptions?.find(o => o._id === id)
                setMods_data(prev => ({ ...prev, model_description: pickModelDescription(option, templateModelDesc?.model_description) }))
              }}
              main={mainAssembly}
              changing={changingMain}
              onToggleChange={() => {
                setChangingMain(!changingMain)
                setToggleSearchPopup(!changingMain)
              }}
            />
          )}
          <TemplateAssemblyList
            isBuyout={assemblyFilter === "Buyout"}
            hideMainLabel={!isBuyoutOrder}
            title={isBuyoutOrder ? undefined : "Sub Assemblies"}
            setToggleSearchPopup={setToggleSearchPopup}
            fetchingTemplateDetails={fetchingTemplateDetails}
            templateDetails={templateDetails}
            addedItems={addedItems}
            templateModelDesc={templateModelDesc}
            selectedAssembly={selectedAssembly}
            setSelectedAssembly={setSelectedAssembly}
            setDisableReset={setDisableReset}
          />
        </div>

        <MODsAssemblyList
          fetchingTemplateDetails={fetchingTemplateDetails}
          addAssemblyData={selectingMain ? replaceMainAssembly : addAssemblyData}
          setToggleSearchPopup={setToggleSearchPopup}
          toggleSearchPopup={toggleSearchPopup}
          assemblyFilter={assemblyFilter}
          title={isBuyoutOrder ? undefined : (selectingMain ? "Select Main Assembly" : "Select Sub Assemblies")}
          subtitle={selectingMain ? "Choose the new main assembly. The current main assembly will be replaced." : undefined}
          highlight={selectingMain}
          actionLabel={selectingMain ? "Select" : "+ Add"}
          onCancel={selectingMain ? () => { setChangingMain(false); setToggleSearchPopup(false) } : undefined}
        />
      </div>
    </div>
  )
}

export default UseEditTemplate